/* ============================================================
 * lbf-collection-bar.js  (v14.0 - cart journey redesign)
 *
 * v14.0 (2026-09-26, build spec 3.3): savings-only copy, never a fee; shows on an
 * empty cart ("FREE delivery from RM150"); also runs on dish pages, where it
 * appears only once the tray's Add button has scrolled out of view; the area
 * picker no longer opens itself on the 3rd frozen dish (toast instead).
 *
 * v13.1 (2026-08-01): MOQ status shows only the existing MOQ message.
 * The region chip is deliberately hidden until the minimum is met.
 * v12.1 (2026-07-09, frozen-moq spec / DECISION_LOG revisions 1-5):
 *   s.kind==='moq' now renders an INFO-tone progressive one-liner
 *   (1 frozen: teach the rule / 2 frozen: cheer the last pack), no
 *   actions, no trailing chip. The gold progress line keeps tracking
 *   free-shipping value underneath via CORE.compute({ignoreMoq:true}).
 *   Fires lbf_moq_bar_view beacon once per frozen-qty change.
 *   Everything else unchanged from v12.0:
 * Collection-page floating shipping bar.
 *
 * Carl-approved mock 2026-07-07 (collection-bar-v12-cart-match-mock.html):
 *   [status BOX - the cart sticky's exact rounded component: i/check
 *    badge + message (wording UNCHANGED) + trailing region CHIP with a
 *    caret that opens the area pills (Option B - replaces the
 *    'Change region' link so the sentence stays on ONE line)]
 *   [main ROW - cart icon + yellow qty badge | bold total | yellow
 *    VIEW CART pill (8px radius, matches cart Checkout) -> cart page]
 *   [thin gold PROGRESS LINE along the bottom edge]
 * Desktop (>=750px): same bar as a centered floating card (720px max,
 * rounded, lifted off the bottom). Mobile: edge-to-edge.
 *
 * Kept from v10 (invisible): request-time optimistic add via prefetched
 * variant map + response fallback; /cart.js reconciles after.
 *
 * Thresholds via shared core window.LBF_SHIPPING. Area persists in
 * Separate regular and frozen regions are owned by the shared shipping core.
 * ============================================================ */
(function () {
  'use strict';
  var lbnT = window.lbnT || function (k, f, v) { return v ? String(f).replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, x) { return Object.prototype.hasOwnProperty.call(v, x) ? String(v[x]) : m; }) : f; };
  if (!window.LBF_SHIPPING) { return; }
  var CORE = window.LBF_SHIPPING;

  var BAR_ID = 'lbf-collection-bar';
  var tagCache = {};       // handle -> { tags, type }
  var prodCache = {};      // handle -> full /products/x.js json
  var variantMap = {};     // variantId -> { handle, price }
  var cartState = null;
  var busy = false;
  var pendingAdds = 0;     // request-time optimistic adds awaiting response
  var previousLane = null;
  var previousSettledState = null;
  var popupTimer = null;
  var regionPromptFired = false;
  var splitPromptFired = false;   /* the 2-box notice opens itself once, on the add that causes it */

  var ICONS = {
    cart: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><circle cx='9' cy='20' r='1.4'/><circle cx='17' cy='20' r='1.4'/><path d='M3 4h2l2.2 11.2a1 1 0 0 0 1 .8h8.6a1 1 0 0 0 1-.8L20 8H6'/></svg>`,
    check: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='3.5' stroke-linecap='round' stroke-linejoin='round'><path d='M5 12.5l5 5L20 7'/></svg>`,
    caret: `<svg viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><path d='M6 9l6 6 6-6'/></svg>`
  };

  function root() { return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; }
  function announceShipStateChange(v) {
    try {
      document.dispatchEvent(new CustomEvent('lbf:shipping-state-change', { detail: { state: v || null, source: 'collection-bar' } }));
    } catch (e) {}
  }
  function handleFromUrl(u) {
    var m = String(u || '').match(/\/products\/([a-z0-9-]+)/i);
    return m ? m[1] : null;
  }

  function fetchCart() {
    return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; });
  }
  function handleOf(li) { return li.handle || (li.url || '').split('/').pop().split('?')[0]; }

  /* ---------- product prefetch (tags + variants for instant adds) ---------- */

  function storeProduct(h, p) {
    if (!p) { tagCache[h] = tagCache[h] || { tags: [], type: '' }; return; }
    var tags = p.tags ? p.tags.map(function (t) { return String(t).toLowerCase(); }) : [];
    tagCache[h] = { tags: tags, type: p.type ? String(p.type).toLowerCase() : '' };
    prodCache[h] = p;
    (p.variants || []).forEach(function (v) {
      variantMap[String(v.id)] = { handle: h, price: v.price != null ? v.price : 0 };
    });
  }

  function fetchProduct(h) {
    return fetch(root() + 'products/' + h + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { storeProduct(h, p); })
      .catch(function () { tagCache[h] = tagCache[h] || { tags: [], type: '' }; });
  }

  function ensureTags(items) {
    var missing = [];
    items.forEach(function (it) { if (!(it.handle in tagCache)) missing.push(it.handle); });
    var uniq = missing.filter(function (h, i) { return h && missing.indexOf(h) === i; });
    return Promise.all(uniq.map(fetchProduct));
  }

  function prefetchVisibleProducts() {
    var seen = {};
    var handles = [];
    var links = document.querySelectorAll('#ProductGridContainer a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var h = handleFromUrl(links[i].getAttribute('href'));
      if (h && !seen[h] && !(h in prodCache)) { seen[h] = 1; handles.push(h); }
      if (handles.length >= 60) break;
    }
    var idx = 0;
    function next() {
      if (idx >= handles.length) return;
      var batch = handles.slice(idx, idx + 6);
      idx += 6;
      Promise.all(batch.map(fetchProduct)).then(next);
    }
    next();
  }

  /* ---------- cart state ---------- */

  function lineFromCartItem(li) {
    var handle = handleOf(li);
    var meta = tagCache[handle] || { tags: [], type: '' };
    var type = (li.product_type ? String(li.product_type).toLowerCase() : '') || meta.type;
    var tags = meta.tags || [];
    var frozenTag = CORE.FROZEN_TAGS.some(function (t) { return tags.indexOf(t) !== -1; });
    return {
      handle: handle, key: li.key || '', qty: li.quantity, linePrice: li.final_line_price,
      variantId: String(li.variant_id || li.id || ''),
      frozenType: type === CORE.FROZEN_TYPE || tags.indexOf('frozen') !== -1,
      frozenTag: frozenTag || type === CORE.FROZEN_TYPE,
      retortType: type === CORE.RETORT_TYPE,
      soloOk: tags.indexOf(CORE.SOLO_TAG) !== -1,
      title: li.product_title || li.title || ''
    };
  }

  function buildState(cart) {
    return {
      items: (cart.items || []).map(lineFromCartItem),
      totalPrice: cart.total_price || 0,
      voucher: CORE.voucherFromCart(cart)
    };
  }

  // Optimistic add at REQUEST time using the prefetched variant map (fast path).
  function optimisticAddByVariant(variantId, qty) {
    var v = variantMap[String(variantId)];
    if (!v || !cartState) return false;
    var meta = tagCache[v.handle] || { tags: [], type: '' };
    var frozen = meta.type === CORE.FROZEN_TYPE || meta.tags.indexOf('frozen') !== -1;
    var existing = null;
    for (var i = 0; i < cartState.items.length; i++) {
      if (cartState.items[i].variantId === String(variantId)) { existing = cartState.items[i]; break; }
    }
    if (existing) {
      existing.qty += qty;
      existing.linePrice += v.price * qty;
    } else {
      cartState.items.push({
        handle: v.handle, qty: qty, linePrice: v.price * qty,
        variantId: String(variantId),
        frozenType: frozen,
        frozenTag: CORE.FROZEN_TAGS.some(function (t) { return meta.tags.indexOf(t) !== -1; }) || frozen,
        retortType: meta.type === CORE.RETORT_TYPE,
        soloOk: meta.tags.indexOf(CORE.SOLO_TAG) !== -1,
        title: meta.title || v.handle || ''
      });
    }
    cartState.totalPrice += v.price * qty;
    render(false);
    return true;
  }

  // Optimistic add from the /cart/add RESPONSE (fallback when variant unknown).
  function optimisticAdd(data) {
    if (!cartState || !data) return;
    var added = Array.isArray(data.items) ? data.items : (data.id ? [data] : []);
    if (!added.length) return;
    added.forEach(function (li) {
      var line = lineFromCartItem(li);
      line.qty = li.quantity || 1;
      line.linePrice = (li.final_line_price != null) ? li.final_line_price : ((li.final_price || li.price || 0) * line.qty);
      cartState.items.push(line);
      cartState.totalPrice += line.linePrice;
    });
    render(false);
  }

  function parseAddBody(init) {
    try {
      var b = init && init.body;
      if (!b) return null;
      if (typeof FormData !== 'undefined' && b instanceof FormData) {
        return { id: b.get('id'), qty: parseInt(b.get('quantity') || '1', 10) || 1 };
      }
      if (typeof URLSearchParams !== 'undefined' && b instanceof URLSearchParams) {
        return { id: b.get('id'), qty: parseInt(b.get('quantity') || '1', 10) || 1 };
      }
      if (typeof b === 'string') {
        if (b.charAt(0) === '{') {
          var j = JSON.parse(b);
          if (j.items && j.items.length) return { id: j.items[0].id, qty: j.items[0].quantity || 1 };
          if (j.id) return { id: j.id, qty: j.quantity || 1 };
          return null;
        }
        var sp = new URLSearchParams(b);
        if (sp.get('id')) return { id: sp.get('id'), qty: parseInt(sp.get('quantity') || '1', 10) || 1 };
      }
    } catch (e) {}
    return null;
  }

  function refresh() {
    if (busy) return;
    if (pendingAdds > 0) { setTimeout(refresh, 250); return; } // don't clobber optimistic state
    busy = true;
    fetchCart().then(function (cart) {
      if (!cart) { busy = false; return; }
      if (pendingAdds > 0) { busy = false; return; }
      cartState = buildState(cart);
      render(true);
      busy = false;
      var missing = (cart.items || []).filter(function (li) { return !(handleOf(li) in tagCache); });
      if (missing.length) {
        ensureTags(missing.map(function (li) { return { handle: handleOf(li) }; }))
          .then(function () { if (pendingAdds === 0) { cartState = buildState(cart); render(true); } });
      }
    }).catch(function () { busy = false; });
  }

  function el() { return document.getElementById(BAR_ID); }
  function isFrozenCart() { return cartState.items.some(function (i) { return i.frozenTag; }); }
  function shortRegion(r) {
    if (r === 'Other WM State') return 'Other WM';
    if (r === 'West Malaysia') return 'West Msia';
    return r || '';
  }

  /* ---------- template pieces (v13 popup entry) ---------- */

  function chipBtn(label, mod) {
    return `<button type='button' class='lbfcb-chipbtn` + (mod ? ' ' + mod : '') + `' data-act='open-popup' aria-haspopup='dialog'>` + label + ` ` + ICONS.caret + `</button>`;
  }

  // sbox: the cart sticky's rounded status box. type: warn | ok | info.
  function sbox(type, msgHTML, trailingHTML) {
    var ic = (type === 'ok') ? ICONS.check : (type === 'danger' ? '!' : 'i');
    return `<div class='lbfcb-sbox is-` + type + `'>` +
      `<span class='lbfcb-sic'>` + ic + `</span>` +
      `<span class='lbfcb-msg'>` + msgHTML + `</span>` +
      (trailingHTML || '') +
    `</div>`;
  }

  /* Compact line names the floor in whole ringgit (approved mock), while the
     amount still owed keeps its sen. Both numbers come off the shared result. */
  function fmtWhole(cents) { return 'RM' + Math.round(Number(cents || 0) / 100); }

  function beacon(event, data) {
    try {
      window.dataLayer = window.dataLayer || [];
      data = data || {}; data.event = event; window.dataLayer.push(data);
    } catch (e) {}
  }

  function schedulePopup(step, trigger, delay, reason) {
    clearTimeout(popupTimer);
    popupTimer = setTimeout(function () {
      if (window.LBF_SHIPPING_POPUP && cartState) {
        window.LBF_SHIPPING_POPUP.open({ cart: cartState, step: step, trigger: trigger, reason: reason || 'manual' });
      }
    }, delay || 0);
  }

  function trackSettledState(s, frozen) {
    /* lane now comes off the shared result, so 'mixed' is a first-class lane
       here and in the cart footer without either file deciding what mixed is. */
    var lane = s.lane || (frozen ? 'cold-chain' : 'regular');
    CORE.trackLane('collection', s);
    if (previousLane && previousLane !== lane) {
      beacon('lbf_lane_switch', { from: previousLane, to: lane, cart_value: s.total, was_free: previousSettledState === 'free' });
    }
    if (s.kind === 'blocked' && previousSettledState !== 'blocked') {
      beacon('lbf_blocked_view', { surface: 'collection', cart_value: s.total, frozen_qty: s.frozenQty });
      try {
        if (!sessionStorage.getItem('lbf-ship-popup-seen')) {
          sessionStorage.setItem('lbf-ship-popup-seen', '1');
          schedulePopup(2, 'auto', 400);
        }
      } catch (e) {}
    }
    /* Carl 2026-09-18: a shopper cannot discover the split by reading the bar -
       whichever item causes it, the notice opens itself at that moment, once per
       session, so nobody meets the second box for the first time at checkout.
       Region prompt wins if both are due: we cannot price the boxes until we know
       where they are, and the region popup leads to this one anyway. */
    if (s.splitWarn && !splitPromptFired && s.kind !== 'moq' && s.kind !== 'blocked' && CORE.getFrozenRegion()) {
      splitPromptFired = true;
      beacon('lbf_split_prompt', { surface: 'collection', cart_value: s.total, lane: s.lane,
        split_total: (s.split && s.split.quotable) ? s.split.total : null });
      try {
        if (!sessionStorage.getItem('lbf-split-popup-seen')) {
          sessionStorage.setItem('lbf-split-popup-seen', '1');
          schedulePopup(2, 'auto', 400, 'split-created');
        }
      } catch (e) { schedulePopup(2, 'auto', 400, 'split-created'); }
    }
    if (frozen && !s.autoFree && s.kind !== 'moq' && s.kind !== 'blocked' && !CORE.getFrozenRegion() && !regionPromptFired) {
      regionPromptFired = true;
      beacon('lbf_region_prompt', { surface: 'collection', cart_value: s.total, frozen_qty: s.frozenQty });
      /* Cart journey 2026-09-26: no auto-open. The toast + the "Choose area" chip do the job. */
    }
    previousLane = lane;
    previousSettledState = s.kind;
  }

  function render(settled) {
    var bar = el();
    if (!bar || !cartState) return;

    var frozen = isFrozenCart();
    /* ready-to-eat with no saved area reads as West Msia (spec 3.3 chip), never a pick prompt */
    var activeRegion = CORE.activeRegion(frozen) || (frozen ? null : 'west-msia');
    var s = CORE.compute(cartState, activeRegion, {});

    /* Dish page (Carl 2026-09-26, Huel pattern): the bar asks for THIS dish, never "View cart".
       It shows once the tray's Add button has scrolled past, whatever is in the cart. */
    if (pdpAddBtn) {
      renderPdp(bar);
      /* the forced Sabah/Sarawak block and the 2-box notice still apply on a dish page */
      if (settled && s.kind !== 'empty') trackSettledState(s, frozen);
      if (window.LBF_SHIPPING_POPUP) window.LBF_SHIPPING_POPUP.update(cartState);
      return;
    }
    /* Collection: nothing in the cart = no bar, no prompt (Carl 2026-09-26). */
    /* motion v1: slide out in 220ms before hiding (was: hidden at once, so the exit never showed) */
    if (s.kind === 'empty') { bar.classList.remove('is-shown'); clearTimeout(bar.__lbfHide); bar.__lbfHide = setTimeout(function () { if (!bar.classList.contains('is-shown')) bar.hidden = true; }, 260); bar.__lbfPct = null; bar.__lbfQty = 0; return; }
    clearTimeout(bar.__lbfHide);
    bar.hidden = false;
    requestAnimationFrame(function () { bar.classList.add('is-shown'); });

    /* status box (cart component) - wording UNCHANGED */
    var top = '';
    var pct = 0;
    var regionButton = '';
    if (s.kind !== 'moq' && s.kind !== 'empty' && s.kind !== 'blocked') {
      regionButton = (s.autoFree && frozen) ? chipBtn(lbnT('ship.chip_west_msia', 'West Msia'), '') :
        (!activeRegion ? (frozen ? chipBtn(lbnT('ship.chip_choose_area', 'Choose area'), 'is-pick') : chipBtn(lbnT('ship.chip_west_msia', 'West Msia'), ''))
          : chipBtn(activeRegion === 'kl-sel' ? lbnT('ship.chip_kl_sel', 'KL/SEL') : (activeRegion === 'wm' ? lbnT('ship.chip_other_wm', 'Other WM') : (activeRegion === 'east-msia' ? lbnT('ship.chip_east_msia', 'East Msia') : lbnT('ship.chip_west_msia', 'West Msia'))), ''));
    }
    if (s.kind === 'moq') {
      /* v13.1: no shipping-region control while the frozen MOQ is unmet.
         The cart cannot check out yet, so the existing MOQ message is the
         only action context shown. */
      top = sbox('info',
        s.have === 1
          ? lbnT('ship.bar_moq_first', "❄️ Adding frozen food? You'll need {{ min }} for cold-chain delivery", { min: `<strong>` + lbnT('ship.bar_moq_first_min', 'at least 3') + `</strong>` })
          : lbnT('ship.bar_moq_last', '❄️ Just {{ more }} frozen dish to unlock cold-chain delivery!', { more: `<strong>` + lbnT('ship.bar_moq_last_more', '1 more') + `</strong>` }),
        regionButton);
      /* Gold line tracks the applicable delivery milestone underneath. */
      var sBg = CORE.compute(cartState, activeRegion, { ignoreMoq: true });
      pct = (sBg.kind === 'free' || sBg.kind === 'mixed-floor') ? 100
        : ((sBg.kind === 'progress' || sBg.kind === 'mixed-progress') ? sBg.pct : 0);
      try {
        if (window.__lbfMoqBarBeacon !== s.have) {
          window.__lbfMoqBarBeacon = s.have;
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({ event: 'lbf_moq_bar_view', frozen_qty: s.have, cart_value: s.total });
          if (window.console && console.log) console.log('[lbf-collection-bar] lbf_moq_bar_view', { frozen_qty: s.have, cart_value: s.total });
        }
      } catch (e) { /* beacons must never break the bar */ }
    } else if (s.kind === 'pickstate') {
      top = sbox('ok', lbnT('ship.bar_cold_unlocked', 'Cold-chain delivery unlocked'), regionButton);
    } else if (s.kind === 'blocked') {
      top = sbox('danger', `<button type='button' class='lbfcb-msgbtn' data-act='open-popup'>` + lbnT('ship.blocked_there', "We can't ship the frozen dishes in your cart there.") + `</button>`, '');
    } else if (s.kind === 'eastmsia') {
      top = sbox('info', lbnT('ship.east_weight_based', 'Weight-based · exact rate at checkout'), regionButton);
    } else if (s.kind === 'mixed-progress') {
      pct = s.pct;
      top = sbox('warn',
        (CORE.seasonalNudge && CORE.seasonalNudge(s)) || (s.tierFree ? lbnT('ship.add_for_free_delivery', 'Add {{ amount }} for free delivery', { amount: `<span class='lbfcb-amt'>` + CORE.fmt(s.remaining) + `</span>` }) : lbnT('ship.add_to_save_on_delivery', 'Add {{ amount }} to save {{ saving }} on delivery', { amount: `<span class='lbfcb-amt'>` + CORE.fmt(s.remaining) + `</span>`, saving: fmtWhole(s.tierSaving) })),
        regionButton);
    } else if (s.kind === 'mixed-floor') {
      pct = 100;
      top = sbox('ok', s.rate === 0 ? lbnT('ship.you_enjoy_free_delivery', 'You enjoy free delivery') : lbnT('ship.you_saved_on_delivery_dot', 'You saved {{ amount }} on delivery.', { amount: fmtWhole(s.baseRate - s.floorRate) }), regionButton);
    } else if (s.kind === 'progress') {
      pct = s.pct;
      top = sbox('warn',
        lbnT('ship.more_for_free_delivery', '{{ amount }} more for FREE delivery', { amount: `<span class='lbfcb-amt'>` + CORE.fmt(s.remaining) + `</span>` }),
        regionButton);
    } else if (s.kind === 'free') {
      pct = 100;
      top = sbox('ok', lbnT('ship.you_enjoy_free_delivery', 'You enjoy free delivery'), regionButton);
    }
    /* a shipping voucher: the win line becomes the one consolidated delivery line; a partial
       voucher below the milestone keeps the milestone nudge, which is the actionable line */
    if (s.voucherNote && (s.voucherNote.kind === 'free' || s.kind === 'free' || s.kind === 'mixed-floor')) {
      pct = 100;
      top = sbox('ok', s.voucherNote.text, regionButton);
    }
    /* FIX B: same truth as the cart page - a split-parcel cart gets a second fee
       at checkout, so the bar must not imply the number it shows is the total. */
    if (s.splitWarn && ['moq', 'blocked', 'pickstate', 'empty'].indexOf(s.kind) === -1) {
      /* Carl 2026-09-18: do NOT lead with the fee here. Seeing two fees before
         you understand why creates an objection; the tap-through explains it. */
      top = sbox('info', lbnT('ship.bar_two_boxes', 'This order ships in 2 boxes'), regionButton);
      pct = 0;
    }
    top = `<div class='lbfcb-pad'>` + top + `</div>`;

    /* main row: cart icon + badge | bold total | VIEW CART pill (cart page, same as live) */
    var row =
      `<div class='lbfcb-row'>` +
        `<span class='lbfcb-ico'>` + ICONS.cart + `<span class='lbfcb-badge'>` + s.totalQty + `</span></span>` +
        `<span class='lbfcb-price'>` + (cartState.voucher && cartState.voucher.order
          ? `<s style='font-size:.72em;font-weight:600;opacity:.55;margin-right:6px'>` + CORE.fmt((s.total || 0) + cartState.voucher.order.cents) + `</s>` : '') +
          CORE.fmt(s.total || 0) + `</span>` +
        `<a class='lbfcb-viewcart' href='` + root() + `cart'>` + lbnT('cart.bar_view_cart', 'View Cart') + `</a>` +
      `</div>`;

    /* thin gold progress line along the bottom edge */
    var pctWas = bar.__lbfPct, qtyWas = bar.__lbfQty || 0;
    var prog = `<div class='lbfcb-prog'><i` + (pctWas != null && pctWas !== pct ? ` class='lbf-grow' style='width:` + pct + `%;--lbf-from:` + pctWas + `%'` : ` style='width:` + pct + `%'`) + `></i></div>`;

    bar.innerHTML = `<div class='lbfcb-wrap'>` + top + row + prog + `</div>`;
    /* motion v1: the gold line animates from its last length (.lbf-grow); the badge bumps when the count goes up */
    var badge = bar.querySelector('.lbfcb-badge');
    if (badge && qtyWas && s.totalQty > qtyWas) badge.classList.add('lbf-bump');
    bar.__lbfPct = pct; bar.__lbfQty = s.totalQty;
    bind(bar);
    if (settled) trackSettledState(s, frozen);
    if (window.LBF_SHIPPING_POPUP) window.LBF_SHIPPING_POPUP.update(cartState);
  }

  /* ---------- dish page gate: the bar waits until the tray's Add button is off screen ---------- */
  var pdpAddBtn = null;
  var pdpAddVisible = true;
  function pdpGateHidden() { return !!pdpAddBtn && pdpAddVisible; }
  function checkPdpAdd() {
    if (!pdpAddBtn) return;
    var r = pdpAddBtn.getBoundingClientRect();
    /* hidden until the shopper has scrolled PAST the Add button (spec 3.2), not merely before reaching it */
    var vis = r.bottom > 0;
    if (vis !== pdpAddVisible) { pdpAddVisible = vis; render(false); }
  }
  function initPdpGate() {
    pdpAddBtn = document.querySelector('.lbf-tray__add');
    if (!pdpAddBtn) return;
    var t = 0;
    var onScroll = function () { var now = Date.now(); if (now - t > 80) { t = now; checkPdpAdd(); } clearTimeout(onScroll._x); onScroll._x = setTimeout(checkPdpAdd, 120); };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function () { checkPdpAdd(); }).observe(pdpAddBtn);
    }
    checkPdpAdd();
  }

  function renderPdp(bar) {
    if (pdpAddVisible) { bar.classList.remove('is-shown'); return; }
    /* Carl 2026-09-26: a sold-out dish never gets an "ADD TO CART" prompt */
    if (document.querySelector('.lbf-tray__row[data-this].is-soldout')) { bar.classList.remove('is-shown'); bar.hidden = true; return; }
    if (!bar.__pdp) {
      bar.__pdp = 1;
      var tray = document.querySelector('.lbf-tray');
      var row = tray && tray.querySelector('.lbf-tray__row[data-this]');
      var img = row && row.querySelector('.lbf-tray__img img');
      var name = row && row.querySelector('.lbf-tray__name');
      bar.classList.add('is-pdp');
      bar.innerHTML = `<div class='lbfcb-wrap'><div class='lbfcb-pdp'>` +
        (img ? `<img class='lbfcb-pdp__img' src='` + img.currentSrc + `' alt='' width='44' height='44'>` : '') +
        `<span class='lbfcb-pdp__t'><span class='lbfcb-pdp__n'></span><span class='lbfcb-pdp__p'></span></span>` +
        `<button type='button' class='lbfcb-pdp__go' data-act='to-tray'>` + lbnT('cart.bar_pdp_add_to_cart', 'ADD TO CART') + `</button></div></div>`;
      bar.querySelector('.lbfcb-pdp__n').textContent = name ? name.textContent : '';
      bar.querySelector('[data-act=to-tray]').addEventListener('click', function () {
        beacon('lbf_pdp_bar_tap', {});
        var t = document.querySelector('.lbf-tray');
        if (t) t.scrollIntoView({ block: 'center' });
        var add = document.querySelector('.lbf-tray__add');
        if (add) { add.classList.remove('is-pulse'); void add.offsetWidth; add.classList.add('is-pulse'); }
      });
    }
    var price = document.querySelector('.lbf-tray__row[data-this] .lbf-tray__price span:last-child');
    bar.querySelector('.lbfcb-pdp__p').textContent = price ? price.textContent : '';
    bar.hidden = false;
    requestAnimationFrame(function () { bar.classList.add('is-shown'); });
  }

  function bind(bar) {
    var toggles = bar.querySelectorAll('[data-act=open-popup]');
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].addEventListener('click', function (e) {
        e.preventDefault();
        var active = CORE.activeRegion(isFrozenCart());
        if (window.LBF_SHIPPING_POPUP) window.LBF_SHIPPING_POPUP.open({ cart: cartState, step: active ? 2 : 1, trigger: 'region', reason: 'manual', opener: this });
      });
    }
  }

  /* ---------- cart event bindings ---------- */

  function bindCartUpdates() {
    ['cart:update', 'cart:refresh', 'cart:item-added', 'cart:item-removed', 'cart-update'].forEach(function (ev) {
      document.addEventListener(ev, function () { setTimeout(refresh, 40); });
    });
    try {
      if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
        window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, function () { setTimeout(refresh, 40); });
      }
    } catch (e) {}
    if (!window.__lbfCartFetchPatched) {
      window.__lbfCartFetchPatched = true;
      var _fetch = window.fetch;
      window.fetch = function (input, init) {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        var handledAtRequest = false;
        if (/\/cart\/add/.test(url) && !(init && init.__lbf)) { /* __lbf writes were drawn at tap time (lbf:cart-intent) */
          // FAST PATH: render the add instantly from the prefetched variant map.
          var body = parseAddBody(init);
          if (body && body.id && optimisticAddByVariant(body.id, body.qty)) {
            handledAtRequest = true;
            pendingAdds++;
          }
        }
        var p = _fetch.apply(this, arguments);
        if (/\/cart\/add/.test(url)) {
          p.then(function (res) {
            if (handledAtRequest) { pendingAdds = Math.max(0, pendingAdds - 1); }
            else if (init && init.__lbf) { /* drawn at tap time; lbf:cart-synced reconciles */ }
            else {
              try { res.clone().json().then(optimisticAdd).catch(function () {}); } catch (e) {}
            }
            setTimeout(refresh, 0);
          }).catch(function () { if (handledAtRequest) { pendingAdds = Math.max(0, pendingAdds - 1); refresh(); } });
        } else if (/\/cart\/(change|update|clear)/.test(url)) {
          p.then(function () { setTimeout(refresh, 0); }).catch(function () {});
        }
        return p;
      };
    }
    /* Carl 2026-09-26: draw every card tap at once (plus AND minus); reconcile when LBF_CART has written it */
    var intents = {};
    document.addEventListener('lbf:cart-intent', function (e) {
      var d = e.detail || {}; if (!d.variantId || !d.delta || !cartState) return;
      var ok = false;
      if (d.delta > 0) ok = optimisticAddByVariant(d.variantId, d.delta);
      else {
        for (var i = 0; i < cartState.items.length; i++) {
          var it = cartState.items[i];
          if (it.variantId === String(d.variantId) && it.linePrice > 0) {
            var unit = it.qty ? it.linePrice / it.qty : 0, take = Math.min(it.qty, -d.delta);
            it.qty -= take; it.linePrice -= unit * take; cartState.totalPrice -= unit * take;
            if (!it.qty) cartState.items.splice(i, 1);
            ok = true; break;
          }
        }
        if (ok) render(false);
      }
      if (ok) { intents[d.variantId] = (intents[d.variantId] || 0) + 1; pendingAdds++; }
    });
    document.addEventListener('lbf:cart-synced', function (e) {
      var v = (e.detail || {}).variantId, n = intents[v] || 0;
      intents[v] = 0; pendingAdds = Math.max(0, pendingAdds - n);
      setTimeout(refresh, 0);
    });
    document.addEventListener('visibilitychange', function () { if (!document.hidden) refresh(); });
    document.addEventListener('lbf:shipping-state-change', function (e) {
      if (!e.detail || e.detail.source !== 'collection-bar') render(false);
    });
    document.addEventListener('lbf:remove-frozen-request', function () {
      if (!cartState || !cartState.items) return;
      var updates = {};
      cartState.items.forEach(function (item) { if (item.frozenTag && item.key) updates[item.key] = 0; });
      if (!Object.keys(updates).length) return;
      fetch(root() + 'cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates })
      }).then(function () {
        beacon('lbf_blocked_resolve', { via: 'removed_frozen' });
        document.dispatchEvent(new CustomEvent('cart:update')); refresh();
      }).catch(function () {});
    });
  }

  function init() {
    if (!el()) return;
    initPdpGate();
    bindCartUpdates();
    refresh();
    var warm = function () { prefetchVisibleProducts(); };
    if ('requestIdleCallback' in window) { requestIdleCallback(warm, { timeout: 2500 }); }
    else { setTimeout(warm, 900); }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
